<script setup lang="ts">
import type { OrderLineFragment } from '#graphql-operations'

const localePath = useLocalePath()
const priceFormatter = usePriceFormatter()
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { ecommerce } = useAnalytics()
const activeOrderStore = useActiveOrderStore()
const uiStore = useUiStore()

async function handleClose() {
  uiStore.closeCartDrawer()
  await nextTick()
}

async function goToCheckout() {
  ecommerce.beginCheckout()
  await handleClose()
  await router.push(localePath('/checkout'))
}

async function goToCart() {
  await handleClose()
  await router.push(localePath('/cart'))
}

const prizeMoney = usePrizeMoney()

const totalQuantity = computed(() => activeOrderStore.activeOrder.data?.totalQuantity ?? 0)
const activeOrderLines = computed(() => (activeOrderStore.activeOrder.data?.lines ?? []) as OrderLineFragment[])

const totalSavings = computed(() => {
  if (!activeOrderLines.value.length)
    return 0

  return activeOrderLines.value.reduce((total, line) => {
    const originalPrice = line.productVariant.priceBeforeDiscount
    if (!originalPrice)
      return total

    const currentPrice = line.unitPrice
    const quantity = line.quantity
    const itemSavings = (originalPrice - currentPrice) * quantity
    return total + (itemSavings > 0 ? itemSavings : 0)
  }, 0)
})

const hasSavings = computed(() => totalSavings.value > 0)

const getRouteBaseName = useRouteBaseName()
const baseRouteName = computed(() => getRouteBaseName(route))
const isCheckoutPage = computed(() => baseRouteName.value === 'checkout')

const subTotal = computed(() => {
  const subTotal = activeOrderStore.activeOrder.data?.subTotal

  if (isCheckoutPage.value) {
    return subTotal
  }

  return activeOrderLines.value.reduce((total, line) => total + line.linePrice, 0)
})
</script>

<template>
  <NDrawer id="cart-drawer" slide-from="right" :show="uiStore.isCartDrawerOpen" :title="t('cart.label')" closable @close="handleClose">
    <div class="mt-2" data-testid="cartDrawer">
      <template v-if="activeOrderStore.activeOrder.data && totalQuantity">
        <div class="flow-root" data-testid="cartItemsList">
          <ul role="list" class="-my-3 divide-y divide-slate-200">
            <CartLineItem
              v-for="line in activeOrderLines"
              :key="line.id"
              :line
              :order="activeOrderStore.activeOrder.data"
              class="px3 py3 sm:px6"
              data-testid="cartLineItem"
              active-order
              @click:link="handleClose"
            />
          </ul>
        </div>

        <!-- Upsell Carousel in drawer -->
        <div class="mt-6 border-t-1 px-3 pb4 pt2 space-y-4 sm:px-6">
          <CartUpsellCarousel
            carousel-item-class="basis-[45%] sm:basis-1/2 pl-2"
            @click:product="handleClose"
          >
            <template #title>
              <div class="flex items-center justify-between">
                <h3 class="text-base text-sm font-semibold">
                  {{ t('product.upsell.title.label') }}
                </h3>
                <NuxtLinkLocale
                  class="flex items-center gap-1 rd-md bg-warning/60 p1 text-xs text-black"
                  to="/collections/top-for-less"
                  @click="handleClose"
                >
                  <Icon name="i-ph:seal-percent" class="text-sm" />
                  {{ t('product.upsell_more.label') }}
                </NuxtLinkLocale>
              </div>
            </template>
          </CartUpsellCarousel>
        </div>
      </template>

      <div v-else class="h-48 flex items-center justify-center text-xl text-slate-400" data-testid="emptyCartMessage">
        {{ t('cart.empty.heading') }}
      </div>
    </div>

    <template v-if="totalQuantity" #footer>
      <div class="pb-safe" data-testid="cartFooter">
        <div class="items-center justify-center gap-1 bg-warning/60 px2 py2 hidden md:flex" data-testid="reservationWarning">
          <Icon name="lucide:timer" class="text-black" />
          <span class="text-center text-xs text-black fw600">
            {{ t('cart.items_not_reserved') }}
          </span>
        </div>
      </div>

      <div class="pb2">
        <div class="hidden sm:block divide-y n-divide-base">
          <div class="flex items-center justify-center gap-1 px2 py2" data-testid="reservationWarning">
            <Icon name="heroicons:gift-top" class="text-error" />
            <span class="text-center text-xs text-primary fw600">
              {{ t('promotion.purchase_competition_prizes', [prizeMoney]) }}
            </span>
          </div>
        </div>

        <NTinyCarousel :items="2" class="h15 text-white sm:hidden">
          <template #slide-0>
            <ProductFeature
              icon="i-heroicons:gift-top"
              :text="t('promotion.purchase_competition_prizes', [prizeMoney])"
              class="px3 sm:px6 [&_span]:text-error"
            />
          </template>
          <template #slide-1>
            <ProductFeature
              icon="lucide:timer"
              :text="t('cart.items_not_reserved')"
              class="px3 sm:px6 [&_span]:text-error"
            />
          </template>
        </NTinyCarousel>

        <div class="border-t n-border-base" data-testid="cartSummary">
          <div class="flex justify-between px3 py0.5 pt2 text-base text-slate-900 sm:px6">
            <p class="text-sm" data-testid="freeShippingLabel" v-text="t('product.free_shipping.label')" />
            <div><Icon name="i-ri:check-fill" n="emerald5 lg" class="text-context" /></div>
          </div>

          <div class="flex justify-between px3 py0.5 text-base text-slate-900 sm:px6">
            <p class="text-sm" data-testid="freeGiftLabel" v-text="t('order.free_gift')" />
            <div><Icon name="i-ri:check-fill" n="emerald5 lg" class="text-context" /></div>
          </div>

          <div class="flex justify-between px3 py0.5 text-base text-slate-900 sm:px6">
            <p class="text-sm" data-testid="complaintResolutionLabel" v-text="t('order.hassle_free_complaint_resolution')" />
            <div><Icon name="i-ri:check-fill" n="emerald5 lg" class="text-context" /></div>
          </div>
          <div v-if="hasSavings" class="mt2 flex justify-between bg-error px3 py2 text-base text-white fw600 sm:px6" data-testid="totalSavings">
            <span class="text">
              {{ t('checkout.you_will_save') }}
            </span>
            <span class="fw600">
              {{ priceFormatter(totalSavings, activeOrderStore.currency) }}
            </span>
          </div>
          <div class="flex justify-between px3 py2 text-base text-slate-900 fw600 sm:px6">
            <p data-testid="subtotalLabel" v-text="t('order.summary.subtotal.label')" />
            <p v-if="subTotal" data-testid="subtotalAmount">
              {{ priceFormatter(subTotal, activeOrderStore.currency) }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-2 px4 py2 pb2 sm:px6">
          <UiButton
            variant="outline"
            size="xl"
            class="w-full justify-center rd-md text-lg fw600 leading-10 hidden md:block"
            data-testid="cartButton"
            @click="goToCart"
          >
            {{ t('checkout.goto_cart.label') }}
          </UiButton>

          <UiButton
            variant="success"
            size="xl"
            class="w-full justify-center rd-md text-lg text-white fw600 leading-10"
            data-testid="checkoutButton"
            @click="goToCheckout"
          >
            {{ t('checkout.goto_checkout.label') }}
          </UiButton>
        </div>
      </div>
    </template>
  </NDrawer>
</template>
